import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { api } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { GlassCard } from '@/components/GlassCard';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ArrowLeft, MapPin, Phone, Clock, Users, Radio, QrCode, Navigation } from 'lucide-react';
import { CATEGORY_META, timeAgo } from '@/lib/utils';
import { toast } from 'sonner';
import { FullPageLoader } from '@/components/Loaders';
import { VenueMap } from '@/components/VenueMap';
import { FollowButton } from '@/components/FollowButton';
import { ThirrstyBadge } from '@/components/ThirrstyBadge';

export default function VenueDetailPage() {
  const { venueId } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [venue, setVenue] = useState(null);
  const [checkins, setCheckins] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [v, c] = await Promise.all([
          api.get(`/venues/${venueId}`),
          api.get(`/venues/${venueId}/checkins`, { params: { limit: 10 } }),
        ]);
        setVenue(v.data);
        setCheckins(c.data);
      } catch { toast.error('Venue not found'); }
      finally { setLoading(false); }
    })();
  }, [venueId]);

  const goLive = () => {
    nav(`/profile/go-live?venue=${venueId}`);
  };

  const openDirections = () => {
    window.open(`https://www.google.com/maps/dir/?api=1&destination=${venue.lat},${venue.lon}`, '_blank', 'noopener');
  };

  if (loading) return <FullPageLoader />;
  if (!venue) return null;

  const meta = CATEGORY_META[venue.category];

  return (
    <div className="min-h-screen">
      <div className="relative h-56 overflow-hidden">
        <img
          src={venue.image_url}
          onError={(e) => { e.currentTarget.src = '/logo-hero.png'; }}
          alt={venue.name}
          className="h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#07070A] via-black/30 to-transparent" />
        <Button
          variant="ghost" size="icon"
          onClick={() => nav(-1)}
          className="absolute left-3 top-3 bg-black/40 text-white hover:bg-black/60 backdrop-blur-sm rounded-full"
          data-testid="venue-back-button"
        >
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div className="absolute bottom-3 left-3 right-3">
          <div className="flex items-center gap-2">
            <Badge className="bg-[#F6B24A]/20 text-[#F6B24A] border border-[#F6B24A]/30">{meta?.label}</Badge>
            {venue.is_21_plus && <Badge className="bg-white/10 text-white/80 border border-white/10">21+</Badge>}
            {venue.subscription_plan && <ThirrstyBadge testId="venue-detail-thirrsty-badge" />}
            {venue.subscription_plan === 'national' && (
              <Badge className="bg-[#2DE2E6]/15 text-[#2DE2E6] border-[#2DE2E6]/30" data-testid="venue-detail-national-badge">★ National Partner</Badge>
            )}
            {venue.subscription_plan === 'regional' && (
              <Badge className="bg-[#2DE2E6]/15 text-[#2DE2E6] border-[#2DE2E6]/30" data-testid="venue-detail-regional-badge">Regional Partner</Badge>
            )}
            {venue.subscription_plan === 'local' && (
              <Badge className="bg-[#F6B24A]/15 text-[#F6B24A] border-[#F6B24A]/30" data-testid="venue-detail-local-badge">Local Partner</Badge>
            )}
          </div>
          <h1 className="mt-2 font-display text-3xl font-semibold text-white" style={{ fontFamily: 'Space Grotesk' }} data-testid="venue-detail-title">{venue.name}</h1>
          <div className="mt-2">
            <FollowButton targetType="venue" targetId={venue.id} />
          </div>
          <p className="text-sm text-white/80">{venue.city}, {venue.state}</p>
        </div>
      </div>

      <main className="mx-auto -mt-4 max-w-2xl space-y-3 px-4 pb-8">
        {venue.discount_text && (
          <GlassCard className="p-4 border-[#F6B24A]/30 bg-[rgba(246,178,74,0.10)]" data-testid="venue-detail-discount">
            <p className="text-xs uppercase tracking-wider text-[#F6B24A]">Active Discount</p>
            <p className="mt-1 text-lg font-semibold text-white">{venue.discount_text}</p>
            <p className="mt-1 text-xs text-white/70">Show your Thirrsty QR at the venue to redeem.</p>
          </GlassCard>
        )}

        <GlassCard className="p-4 space-y-2 text-sm text-white/85">
          {venue.description && <p>{venue.description}</p>}
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={openDirections}
              className="flex min-w-0 flex-1 items-center gap-2 text-left hover:text-[#F6B24A] transition-colors"
              aria-label="Get directions to this venue"
              data-testid="venue-detail-address-link"
            >
              <MapPin className="h-4 w-4 shrink-0 text-white/60" />
              <span className="truncate underline decoration-white/25 underline-offset-2">{venue.address}</span>
            </button>
            <Button
              type="button"
              onClick={openDirections}
              size="sm"
              variant="outline"
              className="h-8 shrink-0 rounded-full border-[#F6B24A]/40 bg-[#F6B24A]/10 px-3 text-xs text-[#F6B24A] hover:bg-[#F6B24A]/20"
              data-testid="venue-detail-directions-button"
            >
              <Navigation className="mr-1.5 h-3.5 w-3.5" /> Get Directions
            </Button>
          </div>
          {venue.hours && <div className="flex items-center gap-2"><Clock className="h-4 w-4 text-white/60" /> {venue.hours}</div>}
          {venue.phone && <div className="flex items-center gap-2"><Phone className="h-4 w-4 text-white/60" /> {venue.phone}</div>}
          <div className="flex items-center gap-2"><Users className="h-4 w-4 text-white/60" /> <span data-testid="venue-detail-checkin-count">{venue.check_in_count || 0}</span> total check-ins</div>
        </GlassCard>

        <VenueMap markers={[{ id: venue.id, name: venue.name, lat: venue.lat, lon: venue.lon, subtitle: venue.address }]} linkToVenue={false} height={200} />

        <div className="grid grid-cols-2 gap-3">
          <Button onClick={() => nav('/profile/qr')} className="h-12 rounded-xl bg-[#F6B24A] text-[#1A1208] hover:bg-[#FFC065] font-semibold" data-testid="venue-detail-check-in-button">
            <QrCode className="mr-2 h-4 w-4" /> Show My QR
          </Button>
          <Button onClick={goLive} variant="outline" className="h-12 rounded-xl border-red-500/40 bg-red-500/10 text-red-200 hover:bg-red-500/15" data-testid="venue-detail-go-live-button">
            <Radio className="mr-2 h-4 w-4" /> Go Live Here
          </Button>
        </div>

        <section>
          <h2 className="mb-2 text-xs uppercase tracking-[0.18em] text-white/60">Recent activity</h2>
          {checkins.length === 0 ? (
            <GlassCard className="p-4 text-center text-white/60 text-sm">No check-ins yet — be the first!</GlassCard>
          ) : (
            <GlassCard className="divide-y divide-white/5">
              {checkins.map((c) => (
                <div key={c.id} className="flex items-center justify-between gap-3 p-3 text-sm">
                  <p className="text-white">{c.user?.display_name || 'Anonymous'}</p>
                  <p className="text-white/50 text-xs">{timeAgo(c.created_at)} ago</p>
                </div>
              ))}
            </GlassCard>
          )}
        </section>

        {!venue.owner_user_id && (
          <GlassCard className="p-4 border-[#F6B24A]/30 bg-[rgba(246,178,74,0.08)]" data-testid="venue-detail-claim-card">
            <p className="text-xs uppercase tracking-wider text-[#F6B24A]">Own or manage this venue?</p>
            <p className="mt-1 text-sm text-white/85">Claim it to unlock the QR scanner, analytics, and discounts. First month free.</p>
            <Button onClick={() => nav(`/profile/claim-venue?venue_id=${encodeURIComponent(venue.id)}`)} className="mt-3 rounded-xl bg-[#F6B24A] text-[#1A1208] hover:bg-[#FFC065] font-semibold" data-testid="venue-detail-claim-button">
              Claim this venue
            </Button>
          </GlassCard>
        )}
      </main>
    </div>
  );
}
